/**
 * Razões de contraste que a documentação cita ao descrever um defeito, medidas
 * de `tokens.css` pela mesma conta do auditor (`ui/estilos/contraste.ts`).
 * Nenhum número é escrito à mão: se o token mudar, o texto muda junto.
 */
import { formatarRazao, razaoRgb, tokensDoTema, TEMAS } from '@/ui/estilos/contraste';
import { paraRgb, type Rgb } from '@/ui/estilos/lerTokens';
import tokensCss from '@/ui/estilos/tokens.css?raw';

type Tokens = ReturnType<typeof tokensDoTema>;

const CLARO = tokensDoTema(tokensCss, TEMAS[0]!);
const ESCURO = tokensDoTema(tokensCss, TEMAS[1]!);

function rgb(tokens: Tokens, token: string): Rgb {
  const hsl = tokens.get(token);
  if (!hsl) throw new Error(`token --${token} ausente de tokens.css`);
  return paraRgb(hsl);
}

/** A cor `frente` com opacidade `alfa`, pintada sobre `fundo`. */
function misturar(frente: Rgb, alfa: number, fundo: Rgb): Rgb {
  return [
    alfa * frente[0] + (1 - alfa) * fundo[0],
    alfa * frente[1] + (1 - alfa) * fundo[1],
    alfa * frente[2] + (1 - alfa) * fundo[2],
  ];
}

/** Texto `--tom` sobre o próprio `--tom` a `alfa`, que fica sobre `--base` (ex.: `text-success bg-success/10`). */
function sobreOProprioTom(tokens: Tokens, tom: string, alfa: number, base: string): number {
  const cor = rgb(tokens, tom);
  return razaoRgb(cor, misturar(cor, alfa, rgb(tokens, base)));
}

/** Texto `--cor` a `alfa` sobre `--fundo` (ex.: `text-muted-foreground/80`). */
function textoTranslucido(tokens: Tokens, cor: string, alfa: number, fundo: string): number {
  const base = rgb(tokens, fundo);
  return razaoRgb(misturar(rgb(tokens, cor), alfa, base), base);
}

/** "3,96:1 no claro e 3,12:1 no escuro". */
function nosDoisTemas(medir: (tokens: Tokens) => number): string {
  return `${formatarRazao(medir(CLARO))} no claro e ${formatarRazao(medir(ESCURO))} no escuro`;
}

export const MEDIDAS = {
  /** `Notificacao` de sucesso: `text-success` sobre `bg-success/10`, num fundo `--background`. */
  notificacaoDeSucesso: nosDoisTemas((t) => sobreOProprioTom(t, 'success', 0.1, 'background')),
  /** `Notificacao` de falha: `text-destructive` sobre `bg-destructive/10`. */
  notificacaoDeFalha: nosDoisTemas((t) => sobreOProprioTom(t, 'destructive', 0.1, 'background')),
  /** `DestructiveActionChip`, tom destrutivo, num cartão. */
  seloDestrutivo: nosDoisTemas((t) => sobreOProprioTom(t, 'destructive', 0.1, 'card')),
  /** `DestructiveActionChip`, tom de atenção, num cartão. */
  seloDeAtencao: nosDoisTemas((t) => sobreOProprioTom(t, 'warning', 0.1, 'card')),
  /** Texto de exemplo do `MultiSelectCombobox`: `text-muted-foreground/80` sobre `--background`. */
  textoDeExemploDoMultiSelect: nosDoisTemas((t) =>
    textoTranslucido(t, 'muted-foreground', 0.8, 'background'),
  ),
  /** Dia de fora do mês do `Calendar`: `text-muted-foreground opacity-50` sobre `--popover`. */
  diaDeForaDoMes: nosDoisTemas((t) => textoTranslucido(t, 'muted-foreground', 0.5, 'popover')),
} as const;
